import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Tag, Truck, Star, Flame, Sparkles, Leaf } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { trackAffiliateClick } from '../hooks/useGoogleAnalytics';
import { trackAffiliateClick as trackMetaViewContent } from '../hooks/useMetaPixel';

const SHIPS_TO_LABELS = {
  'NL/BE': { nl: 'Levert in NL & BE', en: 'Ships to NL & BE' },
  'BE/NL/DE': { nl: 'Levert in BE, NL & DE', en: 'Ships to BE, NL & DE' },
  'BE/NL/LU/DE/AT/FR/FI': {
    nl: 'Levert in BE, NL, LU, DE, AT, FR & FI',
    en: 'Ships to BE, NL, LU, DE, AT, FR & FI',
  },
  'EU': { nl: 'Levert in heel Europa', en: 'Ships across Europe' },
  'EU/USA/AU': { nl: 'Levert in EU, USA & Australië', en: 'Ships to EU, USA & Australia' },
  'Worldwide': { nl: 'Wereldwijde levering', en: 'Worldwide shipping' },
};

const BADGE_CONFIG = {
  bestseller: {
    icon: Star,
    nl: 'Bestseller',
    en: 'Bestseller',
    classes: 'bg-amber-500 text-white',
  },
  trending: {
    icon: Flame,
    nl: 'Trending',
    en: 'Trending',
    classes: 'bg-rose-500 text-white',
  },
  premium: {
    icon: Sparkles,
    nl: 'Premium',
    en: 'Premium',
    classes: 'bg-gray-900 text-white',
  },
  conscious: {
    icon: Leaf,
    nl: 'Conscious',
    en: 'Conscious',
    classes: 'bg-emerald-600 text-white',
  },
};

const ProductCard = ({ product, categoryName, highlight = false }) => {
  const { language, t } = useLanguage();
  const cardRef = useRef(null);
  const [pulse, setPulse] = useState(false);

  // When the visitor lands on this card via /shop/<brand>?highlight=,
  // scroll it into view and trigger a 6-second subtle pulse.
  useEffect(() => {
    if (!highlight || !cardRef.current) return;
    // Defer to next frame so layout/images settle before we scroll.
    const id = window.requestAnimationFrame(() => {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setPulse(true);
    });
    const stop = window.setTimeout(() => setPulse(false), 8200);
    return () => {
      window.cancelAnimationFrame(id);
      window.clearTimeout(stop);
    };
  }, [highlight]);

  const name = language === 'nl' ? product.name_nl : product.name_en;
  const description = language === 'nl' ? product.description_nl : product.description_en;
  const hasDiscount = product.original_price && product.original_price > product.price;
  const discountPercentage = hasDiscount
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;
  const shipsToLabel = product.ships_to && SHIPS_TO_LABELS[product.ships_to]
    ? SHIPS_TO_LABELS[product.ships_to][language] || SHIPS_TO_LABELS[product.ships_to].en
    : null;

  const badgeConfig = product.badge && BADGE_CONFIG[product.badge] ? BADGE_CONFIG[product.badge] : null;
  const BadgeIcon = badgeConfig ? badgeConfig.icon : null;

  // Keyword-rich alt text: "{brand} {category} - {name}" or fallback
  const altText = [product.brand, categoryName, name].filter(Boolean).join(' - ');

  const handleAffiliateClick = () => {
    // Track affiliate click in Google Analytics
    trackAffiliateClick(name, product.brand, product.category_id);
    // Track affiliate click in Meta Pixel (ViewContent = the actual conversion
    // signal we want Meta ads to optimise for).
    trackMetaViewContent(product.brand, categoryName);
  };

  return (
    <a
      ref={cardRef}
      href={product.affiliate_link}
      target="_blank"
      rel="noopener noreferrer nofollow sponsored"
      onClick={handleAffiliateClick}
      aria-label={t(`Shop ${product.brand} — ${name}`, `Shop ${product.brand} — ${name}`)}
      className={`group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-pink-400 focus:ring-offset-2 ${pulse ? 'brand-highlight' : ''}`}
      data-testid={`product-card-${product.id}`}
      data-highlighted={pulse ? 'true' : undefined}
    >
      {/* Image */}
      <div className="relative overflow-hidden aspect-[4/5] shrink-0">
        <img
          src={product.image_url}
          alt={altText}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        {hasDiscount && (
          <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">
            -{discountPercentage}%
          </div>
        )}
        {product.featured && (
          <div className="absolute top-2 left-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white px-2 py-0.5 rounded-full text-xs font-bold flex items-center space-x-1">
            <Tag className="w-2.5 h-2.5" />
            <span>{t('Featured', 'Featured')}</span>
          </div>
        )}
        {badgeConfig && (
          <div
            className={`absolute ${product.featured ? 'top-9' : 'top-2'} left-2 ${badgeConfig.classes} px-2 py-0.5 rounded-full text-xs font-bold flex items-center space-x-1 shadow-sm`}
            data-testid={`product-badge-${product.id}`}
          >
            <BadgeIcon className="w-2.5 h-2.5" />
            <span>{t(badgeConfig.nl, badgeConfig.en)}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3.5 flex flex-col flex-1">
        <div className="text-[10px] text-pink-500 font-semibold uppercase tracking-wide mb-1">
          {product.brand}
        </div>
        <h3 className="text-sm font-bold text-gray-900 mb-1.5 line-clamp-2 leading-snug">{name}</h3>
        <p className="text-xs text-gray-600 mb-3 line-clamp-2 leading-snug">{description}</p>

        {/* Shipping info badge */}
        {shipsToLabel && (
          <div
            className="inline-flex items-center gap-1 self-start mb-2 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[10px] font-medium"
            data-testid={`product-shipping-${product.id}`}
          >
            <Truck className="w-2.5 h-2.5" />
            <span>{shipsToLabel}</span>
          </div>
        )}

        {/* Price */}
        <div className="flex items-baseline space-x-1.5 mb-3">
          {product.price != null && product.price > 0 ? (
            <>
              <span className="text-lg font-bold text-gray-900">
                €{product.price.toFixed(2)}
              </span>
              {hasDiscount && (
                <span className="text-xs text-gray-400 line-through">
                  €{product.original_price.toFixed(2)}
                </span>
              )}
            </>
          ) : (
            <span className="text-sm font-medium text-gray-600 italic">
              {t('Bekijk prijs bij merk', 'See price at brand')}
            </span>
          )}
        </div>

        {/* CTA visual — the whole card is the actual link; this is a visual
            affordance so users still see the familiar "Shop at brand" button. */}
        <span
          className="mt-auto block w-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white text-center py-2 rounded-lg text-xs font-semibold transition-all duration-300 flex items-center justify-center space-x-1.5 group-hover:shadow-lg"
          data-testid={`product-cta-${product.id}`}
        >
          <span>{t(`Shop bij ${product.brand}`, `Shop at ${product.brand}`)}</span>
          <ExternalLink className="w-3 h-3" />
        </span>
      </div>
    </a>
  );
};

export default ProductCard;